/**
 * 거래처 관리 탭 — 일반 사업자 + 공공기관 마스터.
 *
 * 검색·필터(유형) + 페이지네이션 + 행 클릭 → 편집 모달.
 * 새 거래처 등록 → 같은 모달, initial=null.
 */
import React, { useEffect, useState } from "react";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import ActionButton from "../../../../component/common/ActionButton";
import {
    Box, Button, Checkbox, Chip, IconButton, Pagination, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { ds } from "../../../../styles/designSystem";
import AddRoundedIcon           from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon        from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon       from "@mui/icons-material/RefreshRounded";

import { financeApi } from "../../api/financeApi";
import { notify }     from "../../../../util/toast";
import { useMyPermissions } from "../../../../util/useMyPermissions";
import { formatPhone } from "../../../../util/phone";

import VendorEditDialog from "../../components/vendor/VendorEditDialog";
import LoadingDots from "../../../../component/common/LoadingDots";
import ResizableTable from "../../../../component/common/ResizableTable";
import TablePaginator from "../../../../component/common/TablePaginator";

const TYPE_LABEL = { GENERAL: "사업자", PUBLIC: "공공기관" };
const TYPE_COLOR = {
    GENERAL: { bg: "rgba(37,99,235,0.10)", color: "#1d4ed8" },
    PUBLIC:  { bg: "rgba(22,163,74,0.10)", color: "#15803d" },
};

export default function VendorTab() {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("FINANCE_VENDOR");

    const [type, setType]       = useState(null);     // null | GENERAL | PUBLIC
    const [q, setQ]             = useState("");
    const [page, setPage]       = useState(0);
    const size                  = 20;

    const [data, setData]       = useState({ content: [], totalPages: 1, totalElements: 0 });
    const [loading, setLoading] = useState(false);

    const [editing, setEditing] = useState(undefined); // undefined=닫힘, null=새, {…}=수정

    const reload = async () => {
        setLoading(true);
        try {
            const res = await financeApi.listVendors({ type, q: q.trim(), page, size });
            setData(res?.data ?? res ?? { content: [] });
        } catch (e) {
            notify(e?.message || "거래처 조회 실패", "error");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [type, page]);

    const handleSearch = () => {
        if (page === 0) reload();
        else setPage(0);
    };

    // 좌측 체크박스 선택 → 일괄 삭제 (soft delete — 기존 거래내역의 거래처 표시는 유지)
    const [selected, setSelected] = useState(() => new Set());

    const toggleSelect = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const toggleSelectAll = (list) => {
        const targets = list.map(v => v.id);
        setSelected(prev => targets.length > 0 && targets.every(id => prev.has(id))
            ? new Set()
            : new Set(targets));
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        if (!(await confirmDialog({ message: `선택한 거래처 ${selected.size}곳을 삭제하시겠습니까?\n(기존 거래내역의 거래처 표시는 유지됩니다)` }))) return;
        try {
            await Promise.allSettled([...selected].map(id => financeApi.deleteVendor(id)));
            notify("삭제되었습니다", "success");
            setSelected(new Set());
            reload();
        } catch (e) {
            notify(e?.message || "삭제 실패", "error");
        }
    };

    const rows = data?.content || [];
    const totalPages = data?.totalPages || 1;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                {canManage && selected.size > 0 && (
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 2, py: 0.8, bgcolor: "#fef2f2", borderRadius: 1.5,
                    }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>
                            {selected.size}곳 선택됨
                        </Typography>
                        <ActionButton variant="secondary" onClick={handleBulkDelete}
                            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                            label="선택 삭제" sx={{ fontSize: 12, color: "#dc2626" }} />
                        <Chip label="선택 해제" size="small" onClick={() => setSelected(new Set())}
                            sx={{ height: 22, fontSize: 11, cursor: "pointer" }} />
                    </Box>
                )}
            </Stack>

            {/* 필터 */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                    <ToggleButtonGroup
                        size="small" exclusive
                        value={type ?? "ALL"}
                        onChange={(_, v) => { setType(v === "ALL" ? null : v); setPage(0); }}
                        sx={{
                            flexShrink: 0,
                            "& .MuiToggleButton-root": {
                                px: 1.6, py: 0.6, minWidth: 64,
                                textTransform: "none", fontWeight: 700, fontSize: 12,
                                whiteSpace: "nowrap", lineHeight: 2.0,
                                borderRadius: 0.5,
                            },
                        }}>
                        <ToggleButton value="ALL">전체</ToggleButton>
                        <ToggleButton value="GENERAL">사업자</ToggleButton>
                        <ToggleButton value="PUBLIC">공공기관</ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                        size="small" fullWidth
                        placeholder="이름·사업자등록번호·전화·담당자·메모로 검색"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                        InputProps={{
                            startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }} />,
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }} />
                    <ActionButton variant="secondary" onClick={handleSearch} startIcon={false} label="검색" />
                    {canManage && (
                        <ActionButton variant="secondary" onClick={() => setEditing(null)} label="추가" />
                    )}
                    <Tooltip title="새로고침">
                        <IconButton size="small" onClick={reload}>
                            <RefreshRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                </Stack>
            </Paper>

            {/* 표 */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            {canManage && (
                                <TableCell padding="checkbox">
                                    <Checkbox size="small"
                                        checked={rows.length > 0 && rows.every(v => selected.has(v.id))}
                                        indeterminate={selected.size > 0 && !rows.every(v => selected.has(v.id))}
                                        onChange={() => toggleSelectAll(rows)} />
                                </TableCell>
                            )}
                            <TableCell>유형</TableCell>
                            <TableCell>이름 / 기관명</TableCell>
                            <TableCell>사업자등록번호</TableCell>
                            <TableCell>대표자</TableCell>
                            <TableCell>전화번호</TableCell>
                            <TableCell>담당자</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "#94a3b8" }}><LoadingDots/></TableCell></TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "#94a3b8" }}>등록된 거래처가 없습니다.</TableCell></TableRow>
                        ) : rows.map((v) => {
                            const c = TYPE_COLOR[v.type] || TYPE_COLOR.GENERAL;
                            return (
                                <TableRow key={v.id} hover sx={{ cursor: "pointer" }}
                                    onClick={() => setEditing(v)}>
                                    {canManage && (
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox size="small" checked={selected.has(v.id)}
                                                onChange={() => toggleSelect(v.id)} />
                                        </TableCell>
                                    )}
                                    <TableCell align="center">
                                        <Chip size="small" label={TYPE_LABEL[v.type] || v.type}
                                            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: c.bg, color: c.color }} />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{v.name}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12.5, color: "#64748b" }}>{v.bizRegistrationNo || "—"}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12.5, color: "#64748b" }}>{v.representative || "—"}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12.5, color: "#64748b" }}>
                                        {v.phone ? formatPhone(v.phone) : "—"}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12.5, color: "#64748b" }}>
                                        {v.contactPerson
                                            ? `${v.contactPerson}${v.contactPhone ? ` (${formatPhone(v.contactPhone)})` : ""}`
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>
            </TableContainer>

            {/* 페이지 이동 — 표 아래 가운데(전 시스템 공통). */}
            {totalPages > 1 && (
                <TablePaginator
                    totalPages={totalPages}
                    page={page + 1}
                    onPageChange={(p) => setPage(p - 1)}
                />
            )}

            <VendorEditDialog
                open={editing !== undefined}
                initial={editing}
                onClose={() => setEditing(undefined)}
                onSaved={() => { setEditing(undefined); reload(); }}
            />
        </Box>
    );
}
